import { useState, useRef, type ReactNode } from 'react';
import { DualRangeSlider } from '../../components/DualRangeSlider';
import { DndContext } from '@dnd-kit/core';
import {
  NextTickPill,
  TickPredictionCard,
  TickSlider,
  StatusPill,
  LinearBoardSkeleton,
  ServiceHealthSkeleton,
  PipelineHealthSkeleton,
  CompletionChartSkeleton,
  AgentStatsSkeleton,
  FailingTicketsSkeleton,
  TickSliderSkeleton,
  OwnerBadge,
  AssigneeBadge,
  BoardCard,
  PipelineHealthTile,
} from '../Pipeline';
import { Spinner } from '../../components/Spinner';
import { Skeleton } from '../../components/Skeleton';
import { LLMChip, LLMStatusLegend, type AgentLLMStatus } from '../../components/pipeline/LLMChip';
import { SmokeStatusBadge } from '../../components/SmokeStatusBadge';
import { SmokeVerdictPanel } from '../../components/SmokeVerdictPanel';
import { AutoRegisteredBadge } from '../../components/AutoRegisteredBadge';
import { CheapestItinerariesTable } from '../../components/flights/CheapestItinerariesTable';
import { FlightPriceChart } from '../../components/flights/FlightPriceChart';
import { FlightMoversList } from '../../components/flights/FlightMoversList';
import { FlightPairSelect } from '../../components/flights/FlightPairSelect';
import { pairKey } from '../../components/flights/flightPair';
import { FlightRunHealth } from '../../components/flights/FlightRunHealth';
import { FlightFilterBar } from '../../components/flights/FlightFilterBar';
import { WatchListTable } from '../../components/flights/WatchListTable';
import { WatchForm } from '../../components/flights/WatchForm';
import { CarrierFilterChips } from '../../components/flights/CarrierFilterChips';
import { StopoverPresenceToggle } from '../../components/flights/StopoverPresenceToggle';
import type { StopoverPresence } from '../../components/flights/flightFilters';
import {
  VideoTable,
  VideoTitleHeader,
  PostLink,
  TranscriptPanel,
  TranscriptLanguageSwitch,
  NotesEditor,
  TagEditor,
  TranscriptionJobBadge,
  TuningPanel,
} from '../../components/videos';
import {
  nextTickPillFixtures,
  tickPredictionCardFixtures,
  tickSliderFixtures,
  statusPillFixtures,
  ownerBadgeStates,
  assigneeBadgeFixtures,
  boardCardFixtures,
  pipelineHealthTileFixtures,
  spinnerFixtures,
  skeletonFixtures,
  smokeStatusBadgeFixtures,
  smokeVerdictPanelFixtures,
  videoListFixture,
  videoTitleHeaderFixture,
  postLinkFixture,
  videoDetailFixture,
  translationJobFixture,
  tuningFixture,
  jobBadgeFixtures,
  tagSuggestionFixtures,
  tagPendingSuggestionFixtures,
  flightCheapestFixtures,
  flightFreshnessFixtures,
  flightMoversFixtures,
  flightPairFixtures,
  flightSeriesFixtures,
  flightRunFixtures,
  flightFilterBarFixtures,
  watchListFixtures,
  stopoverPresenceFixtures,
  carrierFilterChipsFixtures,
  predictionFunnelFixture,
  funnelDrilldownRowsFixture,
  funnelStageProvenanceFixtures,
  funnelStageLivenessFixtures,
  scoreboardCoverageFixtures,
  pcMonitorListingFixtures,
} from './fixtures';
import type { PcMonitorListing } from '../../types/pcmonitor';
import PredictionFunnel from '../../components/intel/PredictionFunnel';
import FunnelStageRow from '../../components/intel/FunnelStageRow';
import FunnelStageProvenance from '../../components/intel/FunnelStageProvenance';
import FunnelStageLiveness from '../../components/intel/FunnelStageLiveness';
import FunnelDrilldownPanel from '../../components/intel/FunnelDrilldownPanel';
import ScoreboardCoverageCell from '../../components/intel/ScoreboardCoverageCell';
import ListingRow from '../../components/pcmonitor/ListingRow';
import ListingCard from '../../components/pcmonitor/ListingCard';
import ValueGap from '../../components/pcmonitor/ValueGap';
import AskingPrice from '../../components/pcmonitor/AskingPrice';
import PriceDrop from '../../components/pcmonitor/PriceDrop';
import ListingAge from '../../components/pcmonitor/ListingAge';
import VerdictBadge from '../../components/pcmonitor/VerdictBadge';
import StalenessLine from '../../components/pcmonitor/StalenessLine';
import DatasetError from '../../components/pcmonitor/DatasetError';
import EmptyCandidates from '../../components/pcmonitor/EmptyCandidates';
import ServiceGroupHeader from '../../components/ServiceGroupHeader';
import { PropertyCards, PropertyCardRow } from '../../components/payments/PropertyCards';
import { InvoiceQueue } from '../../components/payments/InvoiceQueue';
import { PayReview } from '../../components/payments/PayReview';
import { PayAllConfirm } from '../../components/payments/PayAllConfirm';
import { RunProgress } from '../../components/payments/RunProgress';
import { RunSummary } from '../../components/payments/RunSummary';
import { WaitingForApproval } from '../../components/payments/WaitingForApproval';
import type { OrphanedCard, PayableInvoice, PropertyCardInfo } from '../../types/payments';
import type { RunSuccess } from '../../hooks/usePaymentRun';
import { InvoicesTable } from '../../components/invoices/InvoicesTable';
import { InvoiceFilterBar, type UnitOption } from '../../components/invoices/InvoiceFilterBar';
import type { InvoiceListItem, InvoicePaymentStatus } from '../../types/invoices';
import { CostBreakdownCard } from '../../components/dashboard/CostBreakdownCard';
import { RevenueCostChart } from '../../components/dashboard/RevenueCostChart';
import { ProfitChart } from '../../components/dashboard/ProfitChart';
import type { UnitBreakdown } from '../../lib/dashboard-costs';

const dashboardChartColors = {
  'Dubai JLT Goldcrest': '#f59e0b',
  'Dubai Majestine': '#6366f1',
};

const breakdownFixture: UnitBreakdown = {
  total: 6760,
  prevTotal: 5900,
  rows: [
    {
      prefix: 'mortgage',
      total: 3200,
      share: 0.473,
      trend: 0,
      detail: [
        { category: 'mortgage/interest', total: 2100, share: 0.311, trend: 2.4 },
        { category: 'mortgage/principal', total: 1100, share: 0.163, trend: 0 },
      ],
    },
    {
      prefix: 'utilities',
      total: 2140,
      share: 0.317,
      trend: 14.4,
      detail: [
        { category: 'utilities/dewa', total: 1240, share: 0.183, trend: 8.1 },
        { category: 'utilities/empower', total: 640, share: 0.095, trend: 23.1 },
        { category: 'utilities/internet', total: 260, share: 0.038, trend: null },
      ],
    },
    {
      prefix: 'services',
      total: 1420,
      share: 0.21,
      trend: -12.5,
      detail: [{ category: 'services/cleaning', total: 1420, share: 0.21, trend: -12.5 }],
    },
  ],
};

const revenueCostRowsFixture = [
  {
    month: '2026-06',
    'Dubai JLT Goldcrest': 8200,
    'Dubai JLT Goldcrest costs': 2100,
    'Dubai Majestine': 6100,
    'Dubai Majestine costs': 1800,
  },
  {
    month: '2026-07',
    'Dubai JLT Goldcrest': 9400,
    'Dubai JLT Goldcrest costs': 2400,
    'Dubai Majestine': null,
    'Dubai Majestine costs': 1900,
  },
  {
    month: '2026-08',
    'Dubai JLT Goldcrest': 7800,
    'Dubai JLT Goldcrest costs': 2300,
    'Dubai Majestine': 6900,
    'Dubai Majestine costs': 2000,
  },
];

const profitRowsFixture = [
  { month: '2026-06', 'Dubai JLT Goldcrest': 6100, 'Dubai Majestine': 4300 },
  { month: '2026-07', 'Dubai JLT Goldcrest': 7000, 'Dubai Majestine': -1200 },
  { month: '2026-08', 'Dubai JLT Goldcrest': -900, 'Dubai Majestine': 4900 },
];

// Property cards (CHO-771) — the only card shape the API returns: last4,
// source, configuredAt. Mock data only; the form fields are write-only.
// CHO-799: the section lists payable properties only; a UI-set card stored
// for a non-payable property (Altendorf) renders as an orphaned delete-only row.
const propertyCardDemoProperties = [
  { id: 2, name: 'Dubai JLT Goldcrest' },
  { id: 3, name: 'Dubai Majestine' },
];
const propertyCardDemoCards: Record<number, PropertyCardInfo> = {
  2: { propertyId: 2, last4: '4242', configuredAt: '2026-09-18T10:24:00.000Z', source: 'ui' },
  3: { propertyId: 3, last4: '8811', configuredAt: null, source: 'env' },
};
const propertyCardDemoOrphanedCards: OrphanedCard[] = [
  {
    propertyId: 1,
    propertyName: 'Altendorf',
    card: { propertyId: 1, last4: '9001', configuredAt: '2026-09-10T08:00:00.000Z', source: 'ui' },
  },
];

// Pay all pending (CHO-762) — hardcoded fixtures; the components take props
// only. Altendorf/EHC is the CHF outlier so the per-currency totals stay
// visibly separate; the no-due-date invoice sorts last in run order. CHO-807:
// `biller` is the Noqodi slug (machine payee), `billerName` the human display
// label — the fixtures exercise both-fields-distinct, display-name-only, and
// slug-only rows plus the "None stated" null due date.
const payAllDemoEligible: PayableInvoice[] = [
  {
    id: 'demo-cost-1',
    propertyId: 7,
    propertyName: 'The Lofts',
    biller: 'dewa',
    billerName: 'DEWA',
    amount: 320,
    currency: 'AED',
    date: '2026-09-12',
    dueDate: '2026-09-20',
    eligibility: { payable: true, reasons: [] },
  },
  {
    id: 'demo-cost-2',
    propertyId: 5,
    propertyName: 'Majestine',
    billerName: 'Majestine Service Charge',
    amount: 950,
    currency: 'AED',
    date: '2026-09-10',
    dueDate: '2026-09-25',
    eligibility: { payable: true, reasons: [] },
  },
  {
    id: 'demo-cost-3',
    propertyId: 1,
    propertyName: 'Altendorf',
    biller: 'ehc',
    billerName: 'EHC',
    amount: 150,
    currency: 'CHF',
    date: '2026-09-08',
    eligibility: { payable: true, reasons: [] },
  },
];
const payAllDemoSkipped: PayableInvoice[] = [
  {
    id: 'demo-cost-4',
    propertyId: 9,
    propertyName: 'JLT Goldcrest',
    billerName: 'Empower', // no Noqodi slug configured — the service would refuse to pay
    amount: 1240,
    currency: 'AED',
    date: '2026-09-11',
    dueDate: '2026-09-18',
    eligibility: { payable: false, reasons: ['amount-exceeds-cap:AED 1,240.00 > cap AED 1,000.00'] },
  },
  {
    id: 'demo-cost-5',
    propertyId: 2,
    propertyName: 'Dubai JLT Goldcrest',
    biller: 'asteco', // slug only, no display name attached — renders the slug
    amount: 640,
    currency: 'AED',
    date: '2026-09-14',
    dueDate: '2026-09-22',
    eligibility: { payable: false, reasons: ['blocked:unknown attempt 8c1d2e'] },
  },
];
const runDemoSuccesses: RunSuccess[] = [
  { invoice: payAllDemoEligible[0], attemptId: 'demo-att-1', amount: 320, currency: 'AED' },
];
// Invoices tab (CHO-761) — fixtures anchored relative to today so the overdue
// and no-due-date states stay visible no matter when the gallery is opened.
function isoDaysFromNow(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const invoiceDemoRows: InvoiceListItem[] = [
  {
    id: 1,
    propertyId: 2,
    propertyName: 'Dubai JLT Goldcrest',
    biller: 'DEWA',
    senderDomain: 'dewa.gov.ae',
    accountIdentifier: '1234567890',
    amount: 450.75,
    currency: 'AED',
    invoiceDate: isoDaysFromNow(-20),
    dueDate: isoDaysFromNow(-5),
    paymentStatus: 'unpaid',
    paidAt: null,
    excluded: false,
    sourceEmail: null,
  },
  {
    id: 2,
    propertyId: 3,
    propertyName: 'Dubai Majestine',
    biller: 'Empower',
    senderDomain: 'empower.ae',
    accountIdentifier: '9876543210',
    amount: 612.0,
    currency: 'AED',
    invoiceDate: isoDaysFromNow(-12),
    dueDate: isoDaysFromNow(-2),
    paymentStatus: 'failed',
    paidAt: null,
    excluded: false,
    sourceEmail: null,
  },
  {
    id: 3,
    propertyId: 2,
    propertyName: 'Dubai JLT Goldcrest',
    biller: 'Majestine Service Charge',
    senderDomain: 'majestine.ae',
    accountIdentifier: 'JLT-04-117',
    amount: 950,
    currency: 'AED',
    invoiceDate: isoDaysFromNow(-8),
    dueDate: isoDaysFromNow(10),
    paymentStatus: 'unknown',
    paidAt: null,
    excluded: false,
    sourceEmail: null,
  },
  {
    id: 4,
    propertyId: 3,
    propertyName: 'Dubai Majestine',
    biller: 'Empower',
    senderDomain: 'empower.ae',
    accountIdentifier: '9876543210',
    amount: 588.5,
    currency: 'AED',
    invoiceDate: isoDaysFromNow(-40),
    dueDate: isoDaysFromNow(-15),
    paymentStatus: 'paid',
    paidAt: new Date(Date.now() - 20 * 86400_000).toISOString(),
    excluded: false,
    sourceEmail: null,
  },
  {
    id: 5,
    propertyId: null,
    propertyName: null,
    biller: null,
    senderDomain: 'du.ae',
    accountIdentifier: null,
    amount: 199,
    currency: 'AED',
    invoiceDate: isoDaysFromNow(-3),
    dueDate: null,
    paymentStatus: 'unpaid',
    paidAt: null,
    excluded: false,
    sourceEmail: null,
  },
  {
    id: 6,
    propertyId: 2,
    propertyName: 'Dubai JLT Goldcrest',
    biller: 'DEWA',
    senderDomain: 'dewa.gov.ae',
    accountIdentifier: '1234567890',
    amount: 450.75,
    currency: 'AED',
    invoiceDate: isoDaysFromNow(-20),
    dueDate: isoDaysFromNow(-5),
    paymentStatus: 'unpaid',
    paidAt: null,
    excluded: true,
    sourceEmail: null,
  },
];

const invoiceDemoUnitOptions: UnitOption[] = [
  { key: 'p:2', label: 'Dubai JLT Goldcrest' },
  { key: 'p:3', label: 'Dubai Majestine' },
  { key: 'unassigned', label: 'Unassigned' },
];

const UI_POLICY_TEXT =
  'Every new UI element is its own exported, prop-driven component — no internal fetching; all data arrives via props — and gets a demo entry with hardcoded mock fixtures on this gallery. Fetch-entangled legacy components are grandfathered until extracted.';

// LLMChip (CHO-782) — one fixture per visual state. The quota_unknown detail
// reuses the exact scraper-timeout string from server/__tests__/agents.test.ts.
const llmChipDemoFixtures: { title: string; status: AgentLLMStatus; active?: boolean; activeDetail?: string }[] = [
  {
    title: 'Available',
    status: { command: 'claude', display_name: 'Claude Opus 4.1', available: true, exhausted: false, resetsAt: null, reason: 'ok' },
  },
  {
    title: 'Active',
    status: { command: 'claude', display_name: 'Claude Opus 4.1', available: true, exhausted: false, resetsAt: null, reason: 'ok' },
    active: true,
    activeDetail: 'Claude Opus 4.1 is implementing — CHO-782',
  },
  {
    title: 'Rate-limited',
    status: { command: 'claude', display_name: 'Claude Opus 4.1', available: true, exhausted: true, resetsAt: '2026-09-22T18:00:00Z', reason: 'quota_exhausted' },
  },
  {
    title: 'Quota unknown (grey hollow — scraper broken, agent fine)',
    status: { command: 'kimi', display_name: 'Kimi K2', available: true, exhausted: false, resetsAt: null, reason: 'quota_unknown', detail: 'Quota state unknown — quota scraper error: page.goto: Timeout 30000ms exceeded.' },
  },
  {
    title: 'Offline',
    status: { command: 'codex', display_name: 'Codex GPT-5', available: false, exhausted: null, resetsAt: null, reason: 'probe_failed' },
  },
];

interface SectionDef {
  id: string;
  name: string;
  source: string;
  render: () => ReactNode;
}

function Section({ id, name, source, render }: SectionDef) {
  return (
    <section id={id} className="border border-gray-200 rounded-lg p-4 bg-white scroll-mt-6">
      <h2 className="text-base font-semibold text-gray-800 mb-1">
        {name}
        <span className="block text-xs font-normal text-gray-400 font-mono mt-0.5">{source}</span>
      </h2>
      <div className="mt-3 space-y-4">{render()}</div>
    </section>
  );
}

function DemoBox({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div className="space-y-1">
      {title && <p className="text-xs text-gray-400">{title}</p>}
      <div className="p-3 bg-gray-50 rounded border border-gray-100">{children}</div>
    </div>
  );
}

export default function ComponentGallery() {
  const [selectedTick, setSelectedTick] = useState<unknown>(null);
  const [demoTags, setDemoTags] = useState<string[]>(['ecomm', 'ai-dev']);
  const [demoPair, setDemoPair] = useState<string>(pairKey(flightPairFixtures[0]));
  const [demoFunnelSelected, setDemoFunnelSelected] = useState<{ stageKey: string; view: 'survivors' | 'dropped' } | null>(null);
  const [demoDrilldownOpen, setDemoDrilldownOpen] = useState(false);
  const [demoLang, setDemoLang] = useState<string | null>('es');
  const [dualSliderValue, setDualSliderValue] = useState<[number, number]>([2, 7]);
  const [filterBarFull, setFilterBarFull] = useState(flightFilterBarFixtures.full.value);
  const [filterBarSingle, setFilterBarSingle] = useState(flightFilterBarFixtures.singleNight.value);
  const [filterBarNoInbound, setFilterBarNoInbound] = useState(flightFilterBarFixtures.noInbound.value);
  const [filterBarMissingLeg, setFilterBarMissingLeg] = useState(flightFilterBarFixtures.missingOutboundLeg.value);
  const [filterBarMultiCarrier, setFilterBarMultiCarrier] = useState(flightFilterBarFixtures.multiCarrier.value);
  const [filterBarDirectOnly, setFilterBarDirectOnly] = useState(flightFilterBarFixtures.directOnly.value);
  const [filterBarNoStopovers, setFilterBarNoStopovers] = useState(flightFilterBarFixtures.noStopoversObserved.value);
  const [stopoverPresence, setStopoverPresence] = useState<StopoverPresence>(stopoverPresenceFixtures.any.value);
  const [carrierChipsNone, setCarrierChipsNone] = useState(carrierFilterChipsFixtures.noneSelected.value);
  const [carrierChipsTwo, setCarrierChipsTwo] = useState(carrierFilterChipsFixtures.twoSelected.value);
  const [carrierChipsUnknown, setCarrierChipsUnknown] = useState(carrierFilterChipsFixtures.withUnknown.value);
  const [carrierChipsSingle, setCarrierChipsSingle] = useState(carrierFilterChipsFixtures.singleCarrier.value);
  const [demoIncludePrincipal, setDemoIncludePrincipal] = useState(true);
  const [invoiceDemoDeselectedUnits, setInvoiceDemoDeselectedUnits] = useState<Set<string>>(new Set());
  const [invoiceDemoDeselectedStatuses, setInvoiceDemoDeselectedStatuses] = useState<Set<InvoicePaymentStatus>>(new Set());
  const [invoiceDemoShowExcluded, setInvoiceDemoShowExcluded] = useState(true);
  const historyCache = useRef<Record<string, unknown[]>>({});

  const sections: SectionDef[] = [
    {
      id: 'next-tick-pill',
      name: 'NextTickPill',
      source: 'client/src/pages/Pipeline.tsx',
      render: () => (
        <div className="flex flex-wrap items-center gap-3">
          <DemoBox title="Upcoming">
            <span className="text-sm text-gray-600">Next tick</span>
            <NextTickPill agentTick={nextTickPillFixtures.upcoming} />
          </DemoBox>
          <DemoBox title="Overdue">
            <span className="text-sm text-gray-600">Next tick</span>
            <NextTickPill agentTick={nextTickPillFixtures.overdue} />
          </DemoBox>
          <DemoBox title="No data">
            <span className="text-sm text-gray-600">Next tick</span>
            <NextTickPill agentTick={nextTickPillFixtures.missing} />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'tick-prediction-card',
      name: 'TickPredictionCard',
      source: 'client/src/pages/Pipeline.tsx',
      render: () => (
        <DemoBox>
          <TickPredictionCard prediction={tickPredictionCardFixtures.sample} />
        </DemoBox>
      ),
    },
    {
      id: 'tick-slider',
      name: 'TickSlider',
      source: 'client/src/pages/Pipeline.tsx',
      render: () => (
        <DemoBox>
          <TickSlider
            ticks={tickSliderFixtures.ticks}
            selectedTick={selectedTick}
            onSelectTick={setSelectedTick}
          />
        </DemoBox>
      ),
    },
    {
      id: 'status-pill',
      name: 'StatusPill',
      source: 'client/src/pages/Pipeline.tsx',
      render: () => (
        <div className="flex flex-wrap items-center gap-3">
          <DemoBox title="Running">
            <StatusPill relay={statusPillFixtures.running.relay} queue={statusPillFixtures.running.queue} />
          </DemoBox>
          <DemoBox title="Degraded">
            <StatusPill relay={statusPillFixtures.degraded.relay} queue={statusPillFixtures.degraded.queue} />
          </DemoBox>
          <DemoBox title="Down">
            <StatusPill relay={statusPillFixtures.down.relay} queue={statusPillFixtures.down.queue} />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'owner-badge',
      name: 'OwnerBadge',
      source: 'client/src/pages/Pipeline.tsx',
      render: () => (
        <div className="flex flex-wrap items-stretch gap-2">
          {ownerBadgeStates.map((state) => (
            <DemoBox key={state} title={state}>
              <div className="relative h-6 w-24 bg-white rounded border border-gray-200">
                <OwnerBadge state={state} />
              </div>
            </DemoBox>
          ))}
        </div>
      ),
    },
    {
      id: 'assignee-badge',
      name: 'AssigneeBadge',
      source: 'client/src/pages/Pipeline.tsx',
      render: () => (
        <div className="flex flex-wrap items-center gap-3">
          <DemoBox title="Kimi">
            <AssigneeBadge
              assignee={assigneeBadgeFixtures.kimi.assignee}
              implementer={assigneeBadgeFixtures.kimi.implementer}
            />
          </DemoBox>
          <DemoBox title="Hack bot">
            <AssigneeBadge
              assignee={assigneeBadgeFixtures.hackBot.assignee}
              implementer={assigneeBadgeFixtures.hackBot.implementer}
            />
          </DemoBox>
          <DemoBox title="Unknown">
            <AssigneeBadge
              assignee={assigneeBadgeFixtures.unknown.assignee}
              implementer={assigneeBadgeFixtures.unknown.implementer}
            />
          </DemoBox>
          <DemoBox title="No assignee">
            <AssigneeBadge
              assignee={assigneeBadgeFixtures.none.assignee}
              implementer={assigneeBadgeFixtures.none.implementer}
            />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'board-card',
      name: 'BoardCard',
      source: 'client/src/pages/Pipeline.tsx',
      render: () => (
        <DemoBox>
          <DndContext>
            <ul className="space-y-1.5 max-w-sm">
              <BoardCard
                issue={boardCardFixtures.issue}
                state={boardCardFixtures.state}
                cardStates={{}}
                expandedCardId={null}
                onToggleCard={() => {}}
                historyCache={historyCache}
                activeTasks={new Set()}
                owner={boardCardFixtures.owner}
              />
            </ul>
          </DndContext>
        </DemoBox>
      ),
    },
    {
      id: 'pipeline-health-tile',
      name: 'PipelineHealthTile',
      source: 'client/src/pages/Pipeline.tsx',
      render: () => (
        <div className="space-y-3 max-w-xl">
          <DemoBox title="Healthy">
            <PipelineHealthTile data={pipelineHealthTileFixtures.ok} />
          </DemoBox>
          <DemoBox title="Issues">
            <PipelineHealthTile data={pipelineHealthTileFixtures.issues} />
          </DemoBox>
          <DemoBox title="Stale">
            <PipelineHealthTile data={pipelineHealthTileFixtures.stale} />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'linear-board-skeleton',
      name: 'LinearBoardSkeleton',
      source: 'client/src/pages/Pipeline.tsx',
      render: () => <LinearBoardSkeleton />,
    },
    {
      id: 'service-health-skeleton',
      name: 'ServiceHealthSkeleton',
      source: 'client/src/pages/Pipeline.tsx',
      render: () => <ServiceHealthSkeleton />,
    },
    {
      id: 'service-group-header',
      name: 'ServiceGroupHeader',
      source: 'client/src/components/ServiceGroupHeader.tsx',
      render: () => (
        <div className="space-y-3 max-w-xl">
          <DemoBox title="Intel: Claims (with note)">
            <ServiceGroupHeader group="Intel: Claims" />
          </DemoBox>
          <DemoBox title="System (no note)">
            <ServiceGroupHeader group="System" />
          </DemoBox>
          <DemoBox title="Collapsed with toggle">
            <ServiceGroupHeader group="Intel: Predictions" collapsed onToggle={() => {}} />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'pipeline-health-skeleton',
      name: 'PipelineHealthSkeleton',
      source: 'client/src/pages/Pipeline.tsx',
      render: () => <PipelineHealthSkeleton />,
    },
    {
      id: 'completion-chart-skeleton',
      name: 'CompletionChartSkeleton',
      source: 'client/src/pages/Pipeline.tsx',
      render: () => <CompletionChartSkeleton />,
    },
    {
      id: 'agent-stats-skeleton',
      name: 'AgentStatsSkeleton',
      source: 'client/src/pages/Pipeline.tsx',
      render: () => <AgentStatsSkeleton />,
    },
    {
      id: 'failing-tickets-skeleton',
      name: 'FailingTicketsSkeleton',
      source: 'client/src/pages/Pipeline.tsx',
      render: () => <FailingTicketsSkeleton />,
    },
    {
      id: 'tick-slider-skeleton',
      name: 'TickSliderSkeleton',
      source: 'client/src/pages/Pipeline.tsx',
      render: () => <TickSliderSkeleton />,
    },
    {
      id: 'llm-chip',
      name: 'LLMChip',
      source: 'client/src/components/pipeline/LLMChip.tsx',
      render: () => (
        <div className="flex flex-wrap items-center gap-3">
          {llmChipDemoFixtures.map((f) => (
            <DemoBox key={f.title} title={f.title}>
              <LLMChip
                status={f.status}
                label="Primary"
                active={f.active}
                activeDetail={f.activeDetail}
              />
            </DemoBox>
          ))}
        </div>
      ),
    },
    {
      id: 'llm-status-legend',
      name: 'LLMStatusLegend',
      source: 'client/src/components/pipeline/LLMChip.tsx',
      render: () => (
        <DemoBox>
          <LLMStatusLegend />
        </DemoBox>
      ),
    },
    {
      id: 'spinner',
      name: 'Spinner',
      source: 'client/src/components/Spinner.tsx',
      render: () => (
        <div className="flex flex-wrap items-center gap-3">
          {spinnerFixtures.map((size) => (
            <DemoBox key={size} title={`${size}px`}>
              <Spinner size={size} />
            </DemoBox>
          ))}
        </div>
      ),
    },
    {
      id: 'skeleton',
      name: 'Skeleton',
      source: 'client/src/components/Skeleton.tsx',
      render: () => (
        <div className="space-y-2 max-w-xs">
          {skeletonFixtures.map((props, idx) => (
            <Skeleton key={idx} className={props.className} />
          ))}
        </div>
      ),
    },
    {
      id: 'auto-registered-badge',
      name: 'AutoRegisteredBadge',
      source: 'client/src/components/AutoRegisteredBadge.tsx',
      render: () => (
        <div className="flex flex-wrap items-center gap-3">
          <DemoBox title="Default tooltip">
            <AutoRegisteredBadge />
          </DemoBox>
          <DemoBox title="Custom tooltip">
            <AutoRegisteredBadge title="Custom auto-registered explanation" />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'smoke-status-badge',
      name: 'SmokeStatusBadge',
      source: 'client/src/components/SmokeStatusBadge.tsx',
      render: () => (
        <div className="flex flex-wrap items-center gap-3">
          <DemoBox title="passed">
            <SmokeStatusBadge {...smokeStatusBadgeFixtures.passed} />
          </DemoBox>
          <DemoBox title="failed">
            <SmokeStatusBadge {...smokeStatusBadgeFixtures.failed} />
          </DemoBox>
          <DemoBox title="deferred">
            <SmokeStatusBadge {...smokeStatusBadgeFixtures.deferred} />
          </DemoBox>
          <DemoBox title="not_run">
            <SmokeStatusBadge {...smokeStatusBadgeFixtures.not_run} />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'smoke-verdict-panel',
      name: 'SmokeVerdictPanel',
      source: 'client/src/components/SmokeVerdictPanel.tsx',
      render: () => (
        <div className="space-y-3 max-w-xl">
          <DemoBox title="No verdict">
            <SmokeVerdictPanel detail={smokeVerdictPanelFixtures.not_run} />
          </DemoBox>
          <DemoBox title="Passed">
            <SmokeVerdictPanel detail={smokeVerdictPanelFixtures.passed} />
          </DemoBox>
          <DemoBox title="Failed">
            <SmokeVerdictPanel detail={smokeVerdictPanelFixtures.failed} />
          </DemoBox>
          <DemoBox title="Deferred">
            <SmokeVerdictPanel detail={smokeVerdictPanelFixtures.deferred} />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'video-table',
      name: 'VideoTable',
      source: 'client/src/components/videos/VideoTable.tsx',
      render: () => (
        <DemoBox>
          <VideoTable
            videos={videoListFixture}
            onTranscribe={() => {}}
            onRowClick={() => {}}
          />
        </DemoBox>
      ),
    },
    {
      id: 'video-title-header',
      name: 'VideoTitleHeader',
      source: 'client/src/components/videos/VideoTitleHeader.tsx',
      render: () => (
        <div className="space-y-3 max-w-xl">
          <DemoBox title="With title">
            <VideoTitleHeader {...videoTitleHeaderFixture.withTitle} />
          </DemoBox>
          <DemoBox title="Filename fallback">
            <VideoTitleHeader {...videoTitleHeaderFixture.filenameFallback} />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'post-link',
      name: 'PostLink',
      source: 'client/src/components/videos/PostLink.tsx',
      render: () => (
        <div className="space-y-3 max-w-xl">
          <DemoBox title="With link">
            <PostLink postUrl={postLinkFixture.withLink} />
          </DemoBox>
          <DemoBox title="No link (numeric stem)">
            <PostLink postUrl={postLinkFixture.noLink} />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'transcription-job-badge',
      name: 'TranscriptionJobBadge',
      source: 'client/src/components/videos/TranscriptionJobBadge.tsx',
      render: () => (
        <div className="flex flex-wrap items-center gap-3">
          {jobBadgeFixtures.map((f, i) => (
            <DemoBox key={i} title={f.status ?? 'none'}>
              <TranscriptionJobBadge status={f.status} error={f.error} />
            </DemoBox>
          ))}
        </div>
      ),
    },
    {
      id: 'transcript-panel',
      name: 'TranscriptPanel',
      source: 'client/src/components/videos/TranscriptPanel.tsx',
      render: () => (
        <div className="space-y-3 max-w-2xl">
          <DemoBox title="With transcript">
            <TranscriptPanel
              transcript={videoDetailFixture.transcript}
              job={videoDetailFixture.job}
              onTranscribe={() => {}}
            />
          </DemoBox>
          <DemoBox title="With translation">
            <TranscriptPanel
              transcript={videoDetailFixture.transcript}
              job={videoDetailFixture.job}
              onTranscribe={() => {}}
              languages={videoDetailFixture.available_languages}
              activeLang={demoLang}
              originalLang={videoDetailFixture.language}
              onLanguageChange={setDemoLang}
              translationJob={translationJobFixture}
              onTranslate={() => {}}
            />
          </DemoBox>
          <DemoBox title="Translation failed">
            <TranscriptPanel
              transcript={videoDetailFixture.transcript}
              job={videoDetailFixture.job}
              onTranscribe={() => {}}
              languages={videoDetailFixture.available_languages}
              activeLang={null}
              originalLang={videoDetailFixture.language}
              onLanguageChange={setDemoLang}
              translationJob={{ ...translationJobFixture, status: 'error', error: 'out of memory' }}
              onTranslate={() => {}}
            />
          </DemoBox>
          <DemoBox title="No transcript">
            <TranscriptPanel transcript={null} job={null} onTranscribe={() => {}} />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'transcript-language-switch',
      name: 'TranscriptLanguageSwitch',
      source: 'client/src/components/videos/TranscriptLanguageSwitch.tsx',
      render: () => (
        <div className="space-y-3 max-w-2xl">
          <DemoBox title="ES | EN (ES active)">
            <TranscriptLanguageSwitch
              languages={['es', 'en']}
              active="es"
              original="es"
              onChange={() => {}}
            />
          </DemoBox>
          <DemoBox title="Single language (renders nothing)">
            <TranscriptLanguageSwitch
              languages={['en']}
              active={null}
              original="en"
              onChange={() => {}}
            />
          </DemoBox>
          <DemoBox title="Unknown original">
            <TranscriptLanguageSwitch
              languages={['en']}
              active={null}
              original={null}
              onChange={() => {}}
            />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'dual-range-slider',
      name: 'DualRangeSlider',
      source: 'client/src/components/DualRangeSlider.tsx',
      render: () => (
        <div className="space-y-3 max-w-md">
          <DemoBox title="Default">
            <DualRangeSlider
              label="Day range"
              min={0}
              max={12}
              value={dualSliderValue}
              onChange={setDualSliderValue}
            />
          </DemoBox>
          <DemoBox title="Formatted values">
            <DualRangeSlider
              label="Dates"
              min={0}
              max={12}
              value={[1, 10]}
              onChange={() => {}}
              formatValue={(v) => `${v + 18}/9`}
            />
          </DemoBox>
          <DemoBox title="Disabled">
            <DualRangeSlider
              label="Locked"
              min={0}
              max={10}
              value={[2, 8]}
              onChange={() => {}}
              disabled
            />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'flight-filter-bar',
      name: 'FlightFilterBar',
      source: 'client/src/components/flights/FlightFilterBar.tsx',
      render: () => (
        <div className="space-y-4">
          <DemoBox title="Full data">
            <FlightFilterBar
              options={flightFilterBarFixtures.full.options}
              value={filterBarFull}
              onChange={setFilterBarFull}
              matched={flightFilterBarFixtures.full.matched}
              total={flightFilterBarFixtures.full.total}
            />
          </DemoBox>
          <DemoBox title="Single distinct nights value (slider + caption)">
            <FlightFilterBar
              options={flightFilterBarFixtures.singleNight.options}
              value={filterBarSingle}
              onChange={setFilterBarSingle}
              matched={flightFilterBarFixtures.singleNight.matched}
              total={flightFilterBarFixtures.singleNight.total}
            />
          </DemoBox>
          <DemoBox title="No inbound axis (inbound slider absent)">
            <FlightFilterBar
              options={flightFilterBarFixtures.noInbound.options}
              value={filterBarNoInbound}
              onChange={setFilterBarNoInbound}
              matched={flightFilterBarFixtures.noInbound.matched}
              total={flightFilterBarFixtures.noInbound.total}
            />
          </DemoBox>
          <DemoBox title="Outbound leg absent (disabled option visible)">
            <FlightFilterBar
              options={flightFilterBarFixtures.missingOutboundLeg.options}
              value={filterBarMissingLeg}
              onChange={setFilterBarMissingLeg}
              matched={flightFilterBarFixtures.missingOutboundLeg.matched}
              total={flightFilterBarFixtures.missingOutboundLeg.total}
            />
          </DemoBox>
          <DemoBox title="Multi-carrier selected (Reset enabled)">
            <FlightFilterBar
              options={flightFilterBarFixtures.multiCarrier.options}
              value={filterBarMultiCarrier}
              onChange={setFilterBarMultiCarrier}
              matched={flightFilterBarFixtures.multiCarrier.matched}
              total={flightFilterBarFixtures.multiCarrier.total}
            />
          </DemoBox>
          <DemoBox title="Direct only selected (nights/leg disabled, Reset enabled)">
            <FlightFilterBar
              options={flightFilterBarFixtures.directOnly.options}
              value={filterBarDirectOnly}
              onChange={setFilterBarDirectOnly}
              matched={flightFilterBarFixtures.directOnly.matched}
              total={flightFilterBarFixtures.directOnly.total}
            />
          </DemoBox>
          <DemoBox title="No stopovers observed (slider still rendered)">
            <FlightFilterBar
              options={flightFilterBarFixtures.noStopoversObserved.options}
              value={filterBarNoStopovers}
              onChange={setFilterBarNoStopovers}
              matched={flightFilterBarFixtures.noStopoversObserved.matched}
              total={flightFilterBarFixtures.noStopoversObserved.total}
            />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'stopover-presence-toggle',
      name: 'StopoverPresenceToggle',
      source: 'client/src/components/flights/StopoverPresenceToggle.tsx',
      render: () => (
        <DemoBox>
          <StopoverPresenceToggle value={stopoverPresence} onChange={setStopoverPresence} />
        </DemoBox>
      ),
    },
    {
      id: 'carrier-filter-chips',
      name: 'CarrierFilterChips',
      source: 'client/src/components/flights/CarrierFilterChips.tsx',
      render: () => (
        <div className="space-y-4">
          <DemoBox title="None selected (All airlines)">
            <CarrierFilterChips
              carriers={carrierFilterChipsFixtures.noneSelected.carriers}
              value={carrierChipsNone}
              onChange={setCarrierChipsNone}
            />
          </DemoBox>
          <DemoBox title="Two selected">
            <CarrierFilterChips
              carriers={carrierFilterChipsFixtures.twoSelected.carriers}
              value={carrierChipsTwo}
              onChange={setCarrierChipsTwo}
            />
          </DemoBox>
          <DemoBox title="?? present (rendered as multi)">
            <CarrierFilterChips
              carriers={carrierFilterChipsFixtures.withUnknown.carriers}
              value={carrierChipsUnknown}
              onChange={setCarrierChipsUnknown}
            />
          </DemoBox>
          <DemoBox title="Single carrier">
            <CarrierFilterChips
              carriers={carrierFilterChipsFixtures.singleCarrier.carriers}
              value={carrierChipsSingle}
              onChange={setCarrierChipsSingle}
            />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'flight-cheapest-table',
      name: 'CheapestItinerariesTable',
      source: 'client/src/components/flights/CheapestItinerariesTable.tsx',
      render: () => (
        <div className="space-y-3">
          <DemoBox title="With observations (round-trip / one-way, stopover badge + hotel note)">
            <CheapestItinerariesTable groups={flightCheapestFixtures.groups} />
          </DemoBox>
          <DemoBox title="Mixed fresh / stale (with run caption)">
            <CheapestItinerariesTable
              groups={flightFreshnessFixtures.mixed.groups}
              freshness={flightFreshnessFixtures.mixed.freshness}
              now={flightFreshnessFixtures.now}
            />
          </DemoBox>
          <DemoBox title="All rows from the last run">
            <CheapestItinerariesTable
              groups={flightFreshnessFixtures.allFresh.groups}
              freshness={flightFreshnessFixtures.allFresh.freshness}
              now={flightFreshnessFixtures.now}
            />
          </DemoBox>
          <DemoBox title="No freshness prop (back-compat)">
            <CheapestItinerariesTable groups={flightCheapestFixtures.groups} now={flightFreshnessFixtures.now} />
          </DemoBox>
          <DemoBox title="Filtered to empty">
            <CheapestItinerariesTable groups={[]} emptyMessage="No itineraries match the current filters." />
          </DemoBox>
          <DemoBox title="Cold start">
            <CheapestItinerariesTable groups={[]} />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'notes-editor',
      name: 'NotesEditor',
      source: 'client/src/components/videos/NotesEditor.tsx',
      render: () => (
        <DemoBox>
          <NotesEditor body={videoDetailFixture.notes} onSave={() => {}} />
        </DemoBox>
      ),
    },
    {
      id: 'tag-editor',
      name: 'TagEditor',
      source: 'client/src/components/videos/TagEditor.tsx',
      render: () => (
        <div className="space-y-3 max-w-md">
          <DemoBox title="With suggestions">
            <TagEditor tags={demoTags} onChange={setDemoTags} suggestions={tagSuggestionFixtures.populated} />
          </DemoBox>
          <DemoBox title="Empty suggestions">
            <TagEditor tags={[]} onChange={() => {}} suggestions={tagSuggestionFixtures.empty} />
          </DemoBox>
          <DemoBox title="AI suggestion states (CHO-745)">
            <TagEditor
              tags={demoTags}
              onChange={setDemoTags}
              suggestions={tagSuggestionFixtures.populated}
              acceptedTags={['hooks']}
              pendingSuggestions={tagPendingSuggestionFixtures}
              onDecideSuggestion={() => {}}
            />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'tuning-panel',
      name: 'TuningPanel',
      source: 'client/src/components/videos/TuningPanel.tsx',
      render: () => (
        <DemoBox>
          <TuningPanel
            tuning={tuningFixture}
            onToggleSuggestions={() => {}}
            onMinSamplesChange={() => {}}
          />
        </DemoBox>
      ),
    },
    {
      id: 'flight-price-chart',
      name: 'FlightPriceChart',
      source: 'client/src/components/flights/FlightPriceChart.tsx',
      render: () => (
        <div className="space-y-3">
          <DemoBox title="Three series (EY/EK return + EK stopover)">
            <div className="w-full min-w-[480px]">
              <FlightPriceChart series={flightSeriesFixtures.series} />
            </div>
          </DemoBox>
          <DemoBox title="Empty">
            <FlightPriceChart series={flightSeriesFixtures.empty} />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'flight-movers-list',
      name: 'FlightMoversList',
      source: 'client/src/components/flights/FlightMoversList.tsx',
      render: () => (
        <div className="space-y-3">
          <DemoBox title="Mixed up/down movers">
            <FlightMoversList movers={flightMoversFixtures.movers as any} threshold={flightMoversFixtures.threshold} />
          </DemoBox>
          <DemoBox title="Quiet day">
            <FlightMoversList movers={[]} threshold={25} />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'flight-pair-select',
      name: 'FlightPairSelect',
      source: 'client/src/components/flights/FlightPairSelect.tsx',
      render: () => (
        <div className="space-y-3 max-w-xl">
          <DemoBox title="With movement markers">
            <FlightPairSelect pairs={flightPairFixtures} value={demoPair} onChange={setDemoPair} />
          </DemoBox>
          <DemoBox title="Empty">
            <FlightPairSelect pairs={[]} value="" onChange={() => {}} />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'flight-run-health',
      name: 'FlightRunHealth',
      source: 'client/src/components/flights/FlightRunHealth.tsx',
      render: () => (
        <div className="space-y-3">
          <DemoBox title="Healthy">
            <FlightRunHealth runs={flightRunFixtures.healthy} />
          </DemoBox>
          <DemoBox title="Recent failure (loud banner)">
            <FlightRunHealth runs={flightRunFixtures.failing} />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'watch-list-table',
      name: 'WatchListTable',
      source: 'client/src/components/flights/WatchListTable.tsx',
      render: () => (
        <div className="space-y-3">
          <DemoBox title="Enabled + disabled watches (disabled row muted)">
            <WatchListTable watches={watchListFixtures} onEdit={() => {}} onToggleEnabled={() => {}} />
          </DemoBox>
          <DemoBox title="Empty (no watches yet)">
            <WatchListTable watches={[]} onEdit={() => {}} onToggleEnabled={() => {}} />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'watch-form',
      name: 'WatchForm',
      source: 'client/src/components/flights/WatchForm.tsx',
      render: () => (
        <div className="space-y-3">
          <DemoBox title="Create mode (name editable, no enabled switch)">
            <WatchForm initial={null} submitting={false} error={null} onSubmit={() => {}} onCancel={() => {}} />
          </DemoBox>
          <DemoBox title="Edit mode (name read-only, enabled switch prefilled)">
            <WatchForm
              initial={watchListFixtures[0]}
              submitting={false}
              error={null}
              onSubmit={() => {}}
              onCancel={() => {}}
            />
          </DemoBox>
          <DemoBox title="Tracker CLI stderr relayed verbatim">
            <WatchForm
              initial={null}
              submitting={false}
              error={"!! outbound.to 2026-12-01 is before outbound.from 2026-12-20"}
              onSubmit={() => {}}
              onCancel={() => {}}
            />
          </DemoBox>
          <DemoBox title="Submitting">
            <WatchForm initial={watchListFixtures[1]} submitting error={null} onSubmit={() => {}} onCancel={() => {}} />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'prediction-funnel',
      name: 'PredictionFunnel',
      source: 'client/src/components/intel/PredictionFunnel.tsx',
      render: () => (
        <div className="space-y-3 max-w-3xl">
          <DemoBox title="Full funnel">
            <PredictionFunnel
              stages={predictionFunnelFixture.stages}
              total={predictionFunnelFixture.total}
              asOf={predictionFunnelFixture.as_of}
              onStageClick={(stage, view) => setDemoFunnelSelected({ stageKey: stage.key, view })}
              selectedStage={
                demoFunnelSelected
                  ? {
                      stage: predictionFunnelFixture.stages.find((s) => s.key === demoFunnelSelected.stageKey)!,
                      view: demoFunnelSelected.view,
                    }
                  : null
              }
            />
          </DemoBox>
          <DemoBox title="Loading">
            <PredictionFunnel
              stages={[]}
              total={0}
              asOf={predictionFunnelFixture.as_of}
              loading
              onStageClick={() => {}}
            />
          </DemoBox>
          <DemoBox title="Error">
            <PredictionFunnel
              stages={predictionFunnelFixture.stages}
              total={predictionFunnelFixture.total}
              asOf={predictionFunnelFixture.as_of}
              error="Intel platform unreachable"
              onStageClick={() => {}}
            />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'funnel-stage-row',
      name: 'FunnelStageRow',
      source: 'client/src/components/intel/FunnelStageRow.tsx',
      render: () => (
        <div className="space-y-2 max-w-3xl">
          {predictionFunnelFixture.stages.map((stage) => (
            <DemoBox key={stage.key} title={stage.label}>
              <FunnelStageRow
                stage={stage}
                total={predictionFunnelFixture.total}
                onClickSurvivors={() => {}}
                onClickDrop={() => {}}
              />
            </DemoBox>
          ))}
        </div>
      ),
    },
    {
      id: 'funnel-stage-provenance',
      name: 'FunnelStageProvenance',
      source: 'client/src/components/intel/FunnelStageProvenance.tsx',
      render: () => (
        <div className="space-y-2 max-w-3xl">
          <DemoBox title="Three-model distribution">
            <FunnelStageProvenance
              stageKey="structure_extracted"
              provenance={funnelStageProvenanceFixtures.threeModels}
            />
          </DemoBox>
          <DemoBox title="+N more overflow">
            <FunnelStageProvenance
              stageKey="structure_extracted"
              provenance={funnelStageProvenanceFixtures.overflow}
            />
          </DemoBox>
          <DemoBox title="Deterministic (fallback)">
            <FunnelStageProvenance stageKey="has_figure" />
          </DemoBox>
          <DemoBox title="Not recorded (fallback)">
            <FunnelStageProvenance stageKey="extracted" />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'funnel-stage-liveness',
      name: 'FunnelStageLiveness',
      source: 'client/src/components/intel/FunnelStageLiveness.tsx',
      render: () => (
        <div className="space-y-2 max-w-3xl">
          <DemoBox title="Derived">
            <FunnelStageLiveness liveness={funnelStageLivenessFixtures.derived} />
          </DemoBox>
          <DemoBox title="Unscheduled">
            <FunnelStageLiveness liveness={funnelStageLivenessFixtures.unscheduled} />
          </DemoBox>
          <DemoBox title="OK">
            <FunnelStageLiveness liveness={funnelStageLivenessFixtures.ok} />
          </DemoBox>
          <DemoBox title="Warning (stale)">
            <FunnelStageLiveness liveness={funnelStageLivenessFixtures.warning} />
          </DemoBox>
          <DemoBox title="Down (stale)">
            <FunnelStageLiveness liveness={funnelStageLivenessFixtures.down} />
          </DemoBox>
          <DemoBox title="Running (manual run on unscheduled stage)">
            <FunnelStageLiveness liveness={funnelStageLivenessFixtures.running} />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'funnel-drilldown-panel',
      name: 'FunnelDrilldownPanel',
      source: 'client/src/components/intel/FunnelDrilldownPanel.tsx',
      render: () => (
        <div className="space-y-3 max-w-3xl">
          <DemoBox title="Open panel">
            <button
              type="button"
              onClick={() => setDemoDrilldownOpen(true)}
              className="px-3 py-1.5 rounded text-sm font-medium bg-blue-600 text-white"
            >
              Open drill-down
            </button>
            {demoDrilldownOpen && (
              <FunnelDrilldownPanel
                stage={predictionFunnelFixture.stages[predictionFunnelFixture.stages.length - 1]}
                view="dropped"
                rows={funnelDrilldownRowsFixture}
                total={75}
                onClose={() => setDemoDrilldownOpen(false)}
                onViewChange={() => {}}
              />
            )}
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'scoreboard-coverage-cell',
      name: 'ScoreboardCoverageCell',
      source: 'client/src/components/intel/ScoreboardCoverageCell.tsx',
      render: () => (
        <div className="space-y-2 max-w-md">
          {scoreboardCoverageFixtures.map((fixture) => (
            <DemoBox key={fixture.name} title={fixture.name}>
              <ScoreboardCoverageCell inWindow={fixture.inWindow} extracted={fixture.extracted} />
            </DemoBox>
          ))}
        </div>
      ),
    },
    {
      id: 'pcmonitor-listing-row',
      name: 'PC Monitor: ListingRow',
      source: 'client/src/components/pcmonitor/ListingRow.tsx',
      render: () => (
        <div className="space-y-3 overflow-x-auto">
          {(
            [
              ['Normal (photo, drop, high priority)', pcMonitorListingFixtures.normal],
              ['Missing photo (placeholder, no reflow)', pcMonitorListingFixtures.missingPhoto],
              ['Unresolved price (badge, never a number)', pcMonitorListingFixtures.unresolvedPrice],
              ['Resolved price, no estimate (not a price problem)', pcMonitorListingFixtures.resolvedNoEstimate],
              ['Rejected (reasons shown)', pcMonitorListingFixtures.rejected],
              ['Needs confirmation', pcMonitorListingFixtures.needsConfirmation],
            ] as [string, PcMonitorListing][]
          ).map(([label, listing]) => (
            <DemoBox key={label} title={label}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[820px]">
                  <tbody>
                    <ListingRow listing={listing} />
                  </tbody>
                </table>
              </div>
            </DemoBox>
          ))}
        </div>
      ),
    },
    {
      id: 'pcmonitor-listing-card',
      name: 'PC Monitor: ListingCard',
      source: 'client/src/components/pcmonitor/ListingCard.tsx',
      render: () => (
        <div className="grid gap-3 max-w-sm">
          {(
            [
              ['Normal', pcMonitorListingFixtures.normal],
              ['Missing photo', pcMonitorListingFixtures.missingPhoto],
              ['Unresolved price', pcMonitorListingFixtures.unresolvedPrice],
              ['Resolved price, no estimate', pcMonitorListingFixtures.resolvedNoEstimate],
              ['Rejected', pcMonitorListingFixtures.rejected],
            ] as [string, PcMonitorListing][]
          ).map(([label, listing]) => (
            <DemoBox key={label} title={label}>
              <ListingCard listing={listing} />
            </DemoBox>
          ))}
        </div>
      ),
    },
    {
      id: 'pcmonitor-value-gap',
      name: 'PC Monitor: ValueGap / AskingPrice / PriceDrop / ListingAge',
      source: 'client/src/components/pcmonitor/',
      render: () => (
        <div className="space-y-3">
          <DemoBox title="ValueGap — positive gap (default sort key)">
            <ValueGap listing={pcMonitorListingFixtures.normal} />
          </DemoBox>
          <DemoBox title="ValueGap — unresolved price (CHO-698)">
            <ValueGap listing={pcMonitorListingFixtures.unresolvedPrice} />
          </DemoBox>
          <DemoBox title="ValueGap — resolved price, no estimate (its own label, not the CHO-698 badge)">
            <div className="flex items-center gap-4">
              <ValueGap listing={pcMonitorListingFixtures.resolvedNoEstimate} />
              <AskingPrice listing={pcMonitorListingFixtures.resolvedNoEstimate} />
            </div>
          </DemoBox>
          <DemoBox title="AskingPrice — resolved vs unresolved">
            <div className="flex items-center gap-4">
              <AskingPrice listing={pcMonitorListingFixtures.normal} />
              <AskingPrice listing={pcMonitorListingFixtures.unresolvedPrice} />
            </div>
          </DemoBox>
          <DemoBox title="PriceDrop — latest below a previous price">
            <div className="flex items-center gap-4">
              <PriceDrop history={pcMonitorListingFixtures.normal.price_history} />
              <PriceDrop history={pcMonitorListingFixtures.missingPhoto.price_history} />
            </div>
          </DemoBox>
          <DemoBox title="ListingAge — days since first_seen">
            <div className="flex items-center gap-4 text-sm">
              <ListingAge firstSeen={pcMonitorListingFixtures.normal.first_seen} />
              <ListingAge firstSeen={new Date(Date.now() - 20 * 86_400_000).toISOString()} />
            </div>
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'pcmonitor-verdict-badge',
      name: 'PC Monitor: VerdictBadge',
      source: 'client/src/components/pcmonitor/VerdictBadge.tsx',
      render: () => (
        <div className="flex flex-wrap items-start gap-4">
          <DemoBox title="Qualifying">
            <VerdictBadge verdict="qualifying" rejectionReasons={[]} />
          </DemoBox>
          <DemoBox title="Needs confirmation">
            <VerdictBadge verdict="needs_confirmation" rejectionReasons={[]} />
          </DemoBox>
          <DemoBox title="Rejected with reasons">
            <VerdictBadge
              verdict="rejected"
              rejectionReasons={pcMonitorListingFixtures.rejected.rejection_reasons}
            />
          </DemoBox>
          <DemoBox title="Rejected, unresolved price">
            <VerdictBadge
              verdict="rejected"
              rejectionReasons={pcMonitorListingFixtures.unresolvedPrice.rejection_reasons}
            />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'pcmonitor-page-states',
      name: 'PC Monitor: StalenessLine / DatasetError / EmptyCandidates',
      source: 'client/src/components/pcmonitor/',
      render: () => (
        <div className="space-y-3 max-w-2xl">
          <DemoBox title="StalenessLine — fresh">
            <StalenessLine generatedAt={new Date(Date.now() - 2 * 3_600_000).toISOString()} />
          </DemoBox>
          <DemoBox title="StalenessLine — stale (crawler may be behind)">
            <StalenessLine generatedAt={new Date(Date.now() - 40 * 3_600_000).toISOString()} />
          </DemoBox>
          <DemoBox title="StalenessLine — no timestamp">
            <StalenessLine generatedAt={null} />
          </DemoBox>
          <DemoBox title="DatasetError — unreachable (distinct from empty)">
            <DatasetError
              message="cannot read /Users/chou/claude/data/pc-monitor/listings/state.json: ENOENT: no such file or directory"
              fetchedAt={new Date().toISOString()}
            />
          </DemoBox>
          <DemoBox title="EmptyCandidates — crawler ran, found nothing">
            <EmptyCandidates includeRejected={false} />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'dashboard-cost-breakdown-card',
      name: 'Dashboard: CostBreakdownCard',
      source: 'client/src/components/dashboard/CostBreakdownCard.tsx',
      render: () => (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DemoBox title="With excluded rows + previous-period trend">
            <CostBreakdownCard
              propertyName="Dubai JLT Goldcrest"
              currency="AED"
              breakdown={breakdownFixture}
              excludedCount={14}
            />
          </DemoBox>
          <DemoBox title="No excluded rows, single category">
            <CostBreakdownCard
              propertyName="Altendorf"
              currency="CHF"
              breakdown={{
                total: 1450,
                prevTotal: null,
                rows: [
                  {
                    prefix: 'utilities',
                    total: 1450,
                    share: 1,
                    trend: null,
                    detail: [{ category: 'utilities/ehc', total: 1450, share: 1, trend: null }],
                  },
                ],
              }}
              excludedCount={0}
            />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'dashboard-revenue-cost-chart',
      name: 'Dashboard: RevenueCostChart',
      source: 'client/src/components/dashboard/RevenueCostChart.tsx',
      render: () => (
        <DemoBox title="AED pane — solid revenue lines, dashed cost lines (same hue); Majestine has a revenue gap in 2026-07">
          <RevenueCostChart
            currency="AED"
            rows={revenueCostRowsFixture}
            properties={['Dubai JLT Goldcrest', 'Dubai Majestine']}
            colors={dashboardChartColors}
            height={260}
          />
        </DemoBox>
      ),
    },
    {
      id: 'dashboard-profit-chart',
      name: 'Dashboard: ProfitChart',
      source: 'client/src/components/dashboard/ProfitChart.tsx',
      render: () => (
        <DemoBox title="Negative-profit months (2026-07 Majestine, 2026-08 JLT) render against the zero baseline">
          <ProfitChart
            currency="AED"
            rows={profitRowsFixture}
            properties={['Dubai JLT Goldcrest', 'Dubai Majestine']}
            colors={dashboardChartColors}
            includePrincipal={demoIncludePrincipal}
            onIncludePrincipalChange={setDemoIncludePrincipal}
            height={260}
          />
        </DemoBox>
      ),
    },
    {
      id: 'property-cards',
      name: 'Payments: PropertyCards',
      source: 'client/src/components/payments/PropertyCards.tsx',
      render: () => (
        <div className="space-y-3 max-w-2xl">
          <DemoBox title="Section — one row per payable property plus one orphaned card (CHO-799)">
            <PropertyCards
              properties={propertyCardDemoProperties}
              cards={propertyCardDemoCards}
              orphanedCards={propertyCardDemoOrphanedCards}
              loading={false}
              error={null}
              busyByProperty={{}}
              errorByProperty={{}}
              savedAtByProperty={{}}
              onSetCard={() => {}}
              onDeleteCard={() => {}}
            />
          </DemoBox>
          <DemoBox title="Row — upstream 400 surfaced verbatim next to the form">
            <PropertyCardRow
              propertyId={2}
              propertyName="Dubai JLT Goldcrest"
              card={propertyCardDemoCards[2]}
              busy={false}
              error="expiry must be MM/YY"
              savedAt={null}
              onSetCard={() => {}}
              onDeleteCard={() => {}}
            />
          </DemoBox>
          <DemoBox title="Row — busy (form disabled while saving)">
            <PropertyCardRow
              propertyId={1}
              propertyName="Altendorf"
              card={{ propertyId: 1, last4: null, configuredAt: null, source: 'none' }}
              busy
              error={null}
              savedAt={null}
              onSetCard={() => {}}
              onDeleteCard={() => {}}
            />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'invoice-queue',
      name: 'Payments: InvoiceQueue',
      source: 'client/src/components/payments/InvoiceQueue.tsx',
      render: () => (
        <div className="space-y-3">
          <DemoBox title="Weekly queue — billerName display (slug fallback), null due dates read &quot;None stated&quot; (CHO-807)">
            <InvoiceQueue invoices={[...payAllDemoEligible, ...payAllDemoSkipped]} onPay={() => {}} />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'pay-review',
      name: 'Payments: PayReview',
      source: 'client/src/components/payments/PayReview.tsx',
      render: () => (
        <div className="space-y-3 max-w-xl">
          <DemoBox title="Single-payment confirmation — display biller name, stated due date">
            <PayReview
              invoice={payAllDemoEligible[0]}
              submitting={false}
              error={null}
              onConfirm={() => {}}
              onBack={() => {}}
            />
          </DemoBox>
          <DemoBox title="Invoice whose PDF states no due date — explicit &quot;None stated&quot;, not a bare dash">
            <PayReview
              invoice={payAllDemoEligible[2]}
              submitting={false}
              error={null}
              onConfirm={() => {}}
              onBack={() => {}}
            />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'pay-all-confirm',
      name: 'Payments: PayAllConfirm',
      source: 'client/src/components/payments/PayAllConfirm.tsx',
      render: () => (
        <div className="space-y-3 max-w-2xl">
          <DemoBox title="Multi-currency confirm (3 eligible, no skips — totals never summed across currencies)">
            <PayAllConfirm invoices={payAllDemoEligible} skipped={[]} onConfirm={() => {}} onCancel={() => {}} />
          </DemoBox>
          <DemoBox title="Confirm with skipped invoices, each with its formatted reason">
            <PayAllConfirm
              invoices={[payAllDemoEligible[0], payAllDemoEligible[1]]}
              skipped={payAllDemoSkipped}
              onConfirm={() => {}}
              onCancel={() => {}}
            />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'pay-all-run-progress',
      name: 'Payments: RunProgress / RunSummary',
      source: 'client/src/components/payments/RunProgress.tsx',
      render: () => (
        <div className="space-y-3 max-w-2xl">
          <DemoBox title="Mid-run — waiting for approval (invoice 2 of 3)">
            <RunProgress
              current={payAllDemoEligible[1]}
              position={2}
              total={3}
              successes={runDemoSuccesses}
              totals={{ AED: 320 }}
              onStop={() => {}}
            >
              <WaitingForApproval
                invoice={payAllDemoEligible[1]}
                startedAt={Date.now() - 65_000}
                cardLast4="4242"
                pollError={null}
              />
            </RunProgress>
          </DemoBox>
          <DemoBox title="Mid-run — initiating the last invoice">
            <RunProgress
              current={payAllDemoEligible[2]}
              position={3}
              total={3}
              successes={[
                ...runDemoSuccesses,
                { invoice: payAllDemoEligible[1], attemptId: 'demo-att-2', amount: 950, currency: 'AED' },
              ]}
              totals={{ AED: 1270 }}
              initiating
              onStop={() => {}}
            />
          </DemoBox>
          <DemoBox title="Run summary — completed">
            <RunSummary
              outcome="completed"
              successes={[
                ...runDemoSuccesses,
                { invoice: payAllDemoEligible[1], attemptId: 'demo-att-2', amount: 950, currency: 'AED' },
                { invoice: payAllDemoEligible[2], attemptId: 'demo-att-3', amount: 150, currency: 'CHF' },
              ]}
              totals={{ AED: 1270, CHF: 150 }}
              onBack={() => {}}
            />
          </DemoBox>
          <DemoBox title="Run summary — stopped mid-wait (live attempt called out)">
            <RunSummary
              outcome="stopped"
              successes={runDemoSuccesses}
              totals={{ AED: 320 }}
              inFlight={{ invoice: payAllDemoEligible[1], attemptId: 'demo-att-2' }}
              onBack={() => {}}
            />
          </DemoBox>
          <DemoBox title="Run summary — halted (the halting attempt's AttemptOutcome renders below it)">
            <RunSummary
              outcome="halted"
              successes={runDemoSuccesses}
              totals={{ AED: 320 }}
              haltReason="The payment service is running in dry-run mode — the portal was walked but nothing was submitted. Continuing would have paid the entire queue without moving any money."
            />
          </DemoBox>
        </div>
      ),
    },
    {
      id: 'invoices-table',
      name: 'Invoices: InvoicesTable',
      source: 'client/src/components/invoices/InvoicesTable.tsx',
      render: () => (
        <DemoBox title="All states — overdue unpaid, overdue failed, unknown, paid, no due date (unassigned), excluded revealed (never overdue)">
          <InvoicesTable invoices={invoiceDemoRows} />
        </DemoBox>
      ),
    },
    {
      id: 'invoice-filter-bar',
      name: 'Invoices: InvoiceFilterBar',
      source: 'client/src/components/invoices/InvoiceFilterBar.tsx',
      render: () => (
        <DemoBox title="Unit multi-select (Unassigned last), all four statuses, excluded toggle with count — interactive">
          <InvoiceFilterBar
            unitOptions={invoiceDemoUnitOptions}
            deselectedUnitKeys={invoiceDemoDeselectedUnits}
            onToggleUnit={(key) =>
              setInvoiceDemoDeselectedUnits((prev) => {
                const next = new Set(prev);
                if (next.has(key)) next.delete(key);
                else next.add(key);
                return next;
              })
            }
            deselectedStatuses={invoiceDemoDeselectedStatuses}
            onToggleStatus={(status) =>
              setInvoiceDemoDeselectedStatuses((prev) => {
                const next = new Set(prev);
                if (next.has(status)) next.delete(status);
                else next.add(status);
                return next;
              })
            }
            excludedCount={1}
            showExcluded={invoiceDemoShowExcluded}
            onToggleShowExcluded={() => setInvoiceDemoShowExcluded((v) => !v)}
          />
        </DemoBox>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-100 text-gray-900">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <header className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Component Gallery</h1>
          <p className="mt-2 text-sm text-gray-600 max-w-3xl">{UI_POLICY_TEXT}</p>
        </header>

        <nav className="mb-8 p-4 bg-white rounded-lg border border-gray-200">
          <h2 className="text-sm font-semibold text-gray-700 mb-2">Table of contents</h2>
          <ul className="flex flex-wrap gap-2">
            {sections.map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  className="inline-block px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded text-gray-700"
                >
                  {s.name}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="space-y-6">
          {sections.map((s) => (
            <Section key={s.id} {...s} />
          ))}
        </div>
      </div>
    </div>
  );
}
